import { withJsonFormsControlProps } from "@jsonforms/react";
import { useState } from "react";
import { Toggle } from "pepsico-ds";
import "./PointsLifecycleSettings.scss";
import {
  CRITERIA_OPERATORS,
  POINTS_LIFECYCLE_CRITERIA,
  filterCriteria,
  normalizePointsExpiration,
} from "./pointsLifecycleSettingsUtils";

const getRules = (data = {}) => ({
  windowBased: data?.rules?.window_based || {},
  inactivityBased: data?.rules?.inactivity_based || {},
  pendingPointsVoid: data?.rules?.pending_points_void || {},
  reservationCancellation: data?.rules?.reservation_cancellation || {},
});

const getDisplayValue = (enabled, value) => {
  if (!enabled || value === undefined || value === null) {
    return "";
  }

  return value;
};

const isInvalid = (value, min = 0) =>
  value === undefined || value === null || value === "" || Number(value) < min;

const formatValue = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  return value;
};

const formatEnabled = (value) => (value ? "Yes" : "No");

const PointsLifecycleToggle = ({ checked, disabled, onUpdate }) => (
  <Toggle
    checked={checked}
    disabled={disabled}
    onUpdate={onUpdate}
    size="medium"
    value={checked ? "Yes" : "No"}
  />
);

const PointsLifecycleViewField = ({ label, value }) => (
  <div className="config-drawer-field view">
    <label className="config-drawer-field-label">{label}:</label>
    <span className="config-drawer-field-value">{formatValue(value)}</span>
  </div>
);

export const PointsLifecycleSettingsView = ({ data }) => {
  const normalizedData = normalizePointsExpiration(data);
  const {
    windowBased,
    inactivityBased,
    pendingPointsVoid,
    reservationCancellation,
  } = getRules(normalizedData);
  const selectedCriteriaLabels = POINTS_LIFECYCLE_CRITERIA.filter((criterion) =>
    filterCriteria(inactivityBased.criteria).includes(criterion.id)
  ).map((criterion) => criterion.label);

  return (
    <div className="config-drawer-fields">
      <PointsLifecycleViewField
        label="Fixed Expiration Period"
        value={formatEnabled(windowBased.enabled)}
      />
      <PointsLifecycleViewField
        label="Expiration Period After Earned (days)"
        value={windowBased.enabled ? windowBased.expiration_period_days : "-"}
      />
      <PointsLifecycleViewField
        label="Inactivity Expiration"
        value={formatEnabled(inactivityBased.enabled)}
      />
      <PointsLifecycleViewField
        label="Expiration Period After Inactivity (days)"
        value={
          inactivityBased.enabled ? inactivityBased.expiration_period_days : "-"
        }
      />
      <PointsLifecycleViewField
        label="Criteria Operator"
        value={inactivityBased.enabled ? inactivityBased.criteria_operator : "-"}
      />
      <PointsLifecycleViewField
        label="Inactivity Criteria"
        value={
          inactivityBased.enabled && selectedCriteriaLabels.length
            ? selectedCriteriaLabels.join(", ")
            : "-"
        }
      />
      <PointsLifecycleViewField
        label="Release pending points after (Days)"
        value={pendingPointsVoid.release_after_days}
      />
      <PointsLifecycleViewField
        label="Release reserved points after (Days)"
        value={reservationCancellation.release_after_days}
      />
    </div>
  );
};

const PointsLifecycleNumberField = ({
  disabled,
  error,
  label,
  min = 0,
  onChange,
  placeholder,
  value,
}) => {
  const [touched, setTouched] = useState(false);
  const showError = touched && error;

  return (
    <label
      className={`points-lifecycle-number-field${showError ? " points-lifecycle-number-field--error" : ""}`}
    >
      <span className="points-lifecycle-number-field__label">
        {label}
        <span>*Required</span>
      </span>
      <input
        aria-label={label}
        disabled={disabled}
        min={min}
        onBlur={() => setTouched(true)}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type="number"
        value={value}
      />
      {showError && (
        <span className="points-lifecycle-number-field__error">
          {value !== "" && Number(value) < min
            ? `Value must be ${min} or greater`
            : "This field is required"}
        </span>
      )}
    </label>
  );
};

export const PointsLifecycleSettingsComponent = ({
  data,
  path,
  handleChange,
  enabled,
  visible,
}) => {
  if (visible === false) {
    return null;
  }

  const normalizedData = normalizePointsExpiration(data);
  const {
    windowBased,
    inactivityBased,
    pendingPointsVoid,
    reservationCancellation,
  } = getRules(normalizedData);
  const filteredCriteria = filterCriteria(inactivityBased.criteria);
  const criteriaOperator = CRITERIA_OPERATORS.includes(
    inactivityBased.criteria_operator
  )
    ? inactivityBased.criteria_operator
    : "OR";
  const disabled = !enabled;

  const updatePointsExpiration = (nextPointsExpiration) => {
    handleChange(path, normalizePointsExpiration(nextPointsExpiration));
  };

  const updateWindowBased = (updates) => {
    updatePointsExpiration({
      ...normalizedData,
      rules: {
        ...normalizedData.rules,
        window_based: {
          ...windowBased,
          ...updates,
        },
      },
    });
  };

  const updateInactivityBased = (updates) => {
    updatePointsExpiration({
      ...normalizedData,
      rules: {
        ...normalizedData.rules,
        inactivity_based: {
          ...inactivityBased,
          ...updates,
        },
      },
    });
  };

  const updatePendingPointsVoid = (updates) => {
    updatePointsExpiration({
      ...normalizedData,
      rules: {
        ...normalizedData.rules,
        pending_points_void: {
          ...pendingPointsVoid,
          ...updates,
        },
      },
    });
  };

  const updateReservationCancellation = (updates) => {
    updatePointsExpiration({
      ...normalizedData,
      rules: {
        ...normalizedData.rules,
        reservation_cancellation: {
          ...reservationCancellation,
          ...updates,
        },
      },
    });
  };

  const handleWindowToggle = (checked) => {
    updateWindowBased(
      checked
        ? { enabled: true }
        : { enabled: false, expiration_period_days: undefined }
    );
  };

  const handleInactivityToggle = (checked) => {
    updateInactivityBased(
      checked
        ? {
            enabled: true,
            criteria_operator: criteriaOperator,
          }
        : {
            enabled: false,
            expiration_period_days: undefined,
            criteria_operator: undefined,
            criteria: undefined,
          }
    );
  };

  const handleCriteriaToggle = (criterion, checked) => {
    const nextCriteria = checked
      ? [...filteredCriteria, criterion]
      : filteredCriteria.filter((item) => item !== criterion);

    updateInactivityBased({
      criteria: nextCriteria,
    });
  };

  return (
    <div className="points-lifecycle-settings">
      <section className="points-lifecycle-card">
        <div className="points-lifecycle-card__header">
          <h3>Fixed Expiration Period</h3>
          <PointsLifecycleToggle
            checked={windowBased.enabled}
            disabled={disabled}
            onUpdate={handleWindowToggle}
          />
        </div>

        <PointsLifecycleNumberField
          label="Expiration Period After Earned (days)"
          min={1}
          value={getDisplayValue(
            windowBased.enabled,
            windowBased.expiration_period_days
          )}
          onChange={(value) =>
            updateWindowBased({
              expiration_period_days: value,
            })
          }
          placeholder="Enter value"
          disabled={disabled || !windowBased.enabled}
          error={
            windowBased.enabled && isInvalid(windowBased.expiration_period_days, 1)
          }
        />
      </section>

      <section className="points-lifecycle-card">
        <div className="points-lifecycle-card__header">
          <h3>Inactivity Expiration</h3>
          <PointsLifecycleToggle
            checked={inactivityBased.enabled}
            disabled={disabled}
            onUpdate={handleInactivityToggle}
          />
        </div>

        <PointsLifecycleNumberField
          label="Expiration Period After Inactivity (days)"
          min={1}
          value={getDisplayValue(
            inactivityBased.enabled,
            inactivityBased.expiration_period_days
          )}
          onChange={(value) =>
            updateInactivityBased({
              expiration_period_days: value,
            })
          }
          placeholder="Enter value"
          disabled={disabled || !inactivityBased.enabled}
          error={
            inactivityBased.enabled &&
            isInvalid(inactivityBased.expiration_period_days, 1)
          }
        />

        <div className="points-lifecycle-conditions">
          <h4>Inactivity Conditions</h4>

          {POINTS_LIFECYCLE_CRITERIA.map((criterion, index) => (
            <div className="points-lifecycle-condition" key={criterion.id}>
              <div className="points-lifecycle-condition__row">
                <span>{criterion.label}</span>
                <PointsLifecycleToggle
                  checked={
                    inactivityBased.enabled &&
                    filteredCriteria.includes(criterion.id)
                  }
                  disabled={disabled || !inactivityBased.enabled}
                  onUpdate={(checked) => handleCriteriaToggle(criterion.id, checked)}
                />
              </div>

              {index < POINTS_LIFECYCLE_CRITERIA.length - 1 && (
                <div className="points-lifecycle-operator">
                  <span />
                  <select
                    aria-label={`Criteria operator after ${criterion.label}`}
                    disabled={disabled || !inactivityBased.enabled}
                    onChange={(event) =>
                      updateInactivityBased({
                        criteria_operator: event.target.value,
                      })
                    }
                    value={criteriaOperator}
                  >
                    {CRITERIA_OPERATORS.map((operator) => (
                      <option key={operator} value={operator}>
                        {operator}
                      </option>
                    ))}
                  </select>
                  <span />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="points-lifecycle-card">
        <div className="points-lifecycle-card__header">
          <h3>Pending Points Void</h3>
        </div>

        <PointsLifecycleNumberField
          label="Release pending points after (Days)"
          value={pendingPointsVoid.release_after_days ?? ""}
          onChange={(value) =>
            updatePendingPointsVoid({
              release_after_days: value,
            })
          }
          placeholder="Enter value"
          disabled={disabled}
          error={isInvalid(pendingPointsVoid.release_after_days)}
        />
      </section>

      <section className="points-lifecycle-card">
        <div className="points-lifecycle-card__header">
          <h3>Reservation Cancellation</h3>
        </div>

        <PointsLifecycleNumberField
          label="Release reserved points after (Days)"
          value={reservationCancellation.release_after_days ?? ""}
          onChange={(value) =>
            updateReservationCancellation({
              release_after_days: value,
            })
          }
          placeholder="Enter value"
          disabled={disabled}
          error={isInvalid(reservationCancellation.release_after_days)}
        />
      </section>
    </div>
  );
};

export const PointsLifecycleSettingsControl = withJsonFormsControlProps(
  PointsLifecycleSettingsComponent
);
